// @ts-strict-ignore
import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useAtomValue } from 'jotai'
import { UseFormReturn } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { toast } from '@node-space/ui-components'
import { logSentryError } from '@node-space/utils'
import { ERROR_STATUS_CODES } from 'constants/General'
import { useCreateContactBankAccountMutation } from 'hooks/mutations/Contacts/useCreateContactBankAccount'
import { useCreateContactBankAccount2FAMutation } from 'hooks/mutations/Contacts/useCreateContactBankAccount2FAMutation'
import { usePutContactInstrumentMutation } from 'hooks/mutations/usePutContactInstrumentMutation'
import { useTwoFAContext } from 'hooks/useTwoFAContext'
import { contactAtom, contactInstrumentAtom } from 'pages/Contacts/atoms/contactAtoms'
import {
  ContactFiatInstrumentFormData,
  ContactFiatInstrumentFormKeys,
} from 'pages/Contacts/schemas/contactFiatInstrumentSchema'
import { getRequestPayload } from 'pages/Contacts/utils/fiatInstruments'
import { reactQueryName } from 'reactQueryKeys/reactQueryKeys'
import { BaseErrorResponse } from 'types/beneficiaries'
import { ContactInstrumentProps, ContactPaymentInstrument } from 'types/contacts'
import { TwoFAAuthRequest } from 'types/generics'

interface UseCreateContactBankAccountProps {
  formValues?: ContactFiatInstrumentFormData
  form?: UseFormReturn<ContactFiatInstrumentFormData>
  onSubmit2FASuccess: (paymentInstrument: ContactPaymentInstrument) => void
}

export const useCreateContactBankAccount = ({
  formValues,
  form,
  onSubmit2FASuccess,
}: UseCreateContactBankAccountProps) => {
  const { t } = useTranslation()
  const { twoFactorEnabled } = useTwoFAContext()
  const queryClient = useQueryClient()

  const contact = useAtomValue(contactAtom)
  const contactInstrument = useAtomValue(contactInstrumentAtom)

  const [calloutError, setCalloutError] = useState<string | null>(null)

  const {
    mutate: submitBankAccount,
    isPending: isSubmittingBankAccount,
    error: submitBankAccountError,
  } = useCreateContactBankAccountMutation()

  const { mutate: submit2FA, isPending: isSubmitting2FA } = useCreateContactBankAccount2FAMutation()

  const isEditingContactInstrument = !!contactInstrument?.id

  const { mutate: updateBankAccount, isPending: isUpdatingBankAccount } =
    usePutContactInstrumentMutation()

  const onSubmitBankAccount = () => {
    setCalloutError(null)
    form?.clearErrors()

    if (!contact.id) {
      setCalloutError(t('contacts.missingContactError'))

      logSentryError(`Error from ContactBankAccount - missing contact id`, {
        description: formValues,
      })

      return
    }

    const requestPayload = getRequestPayload(contact, formValues)

    if (isEditingContactInstrument) {
      const instrumentRequestPayload: ContactInstrumentProps = {
        instrumentId: contactInstrument?.id,
        contactId: contact?.id,
        instrument: requestPayload,
      }
      updateBankAccount(instrumentRequestPayload, {
        onError: onPreAuthErrorHandler,
      })
    } else {
      submitBankAccount(
        { contactId: contact?.id, request: requestPayload },
        {
          onError: onPreAuthErrorHandler,
        }
      )
    }
  }

  const onPreAuthErrorHandler = (error: BaseErrorResponse) => {
    const { twoFactorCode } = form?.getValues() ?? {}
    const { challenge, callbackUrl } = error?.data ?? {}

    const isAuthRequired = error?.status === ERROR_STATUS_CODES?.AUTH_REQUIRED

    if (isAuthRequired) {
      onSubmit2FARequest({
        challenge: challenge?.value || '',
        tokenResponse: twoFactorCode || '',
        callbackUrl: callbackUrl || '',
      })
    } else if (error) {
      handleGenericError('onSubmitBankAccount', error)
    }
  }

  const onPostAuthErrorHandler = (error: BaseErrorResponse) => {
    const isAuthError = error?.status === ERROR_STATUS_CODES?.FORBIDDEN

    if (isAuthError) {
      form?.setError('twoFactorCode', {
        message: twoFactorEnabled ? t('authCodeInvalid') : t('authPasswordInvalid'),
      })
    } else if (error) {
      handleGenericError('onSubmit2FARequest', error)
    }
  }

  const handleGenericError = (errorTitle: string, error: BaseErrorResponse) => {
    if (error?.data?.errorList) {
      error.data.errorList.forEach(err => {
        form?.setError(err?.parameter as ContactFiatInstrumentFormKeys, { message: error?.message })
      })
    } else {
      setCalloutError(error?.data?.message ?? t('createBankAccountError'))
      logSentryError(`Error from ContactBankAccount - ${errorTitle}`, error, {
        contactId: contact?.id,
        currency: formValues?.currency,
        description: formValues?.description,
      })
    }
  }

  const onSubmit2FARequest = (payload: TwoFAAuthRequest) => {
    if (!payload?.callbackUrl) return

    submit2FA(payload, {
      onSuccess: (data: ContactPaymentInstrument) => {
        onSubmit2FASuccess?.(data)
        queryClient.invalidateQueries({ queryKey: [reactQueryName.CONTACT_INSTRUMENTS] })
        queryClient.invalidateQueries({ queryKey: [reactQueryName.BENEFICIARIES] })
        if (isEditingContactInstrument) {
          toast({
            header: t('contacts.bankAccountUpdatedSuccessfully'),
            variant: 'success',
          })
        }
      },
      onError: onPostAuthErrorHandler,
    })
  }

  return {
    isSubmittingBankAccount,
    submitBankAccountError,
    isSubmitting2FA,
    onSubmitBankAccount,
    calloutError,
    isLoading: isSubmittingBankAccount || isSubmitting2FA || isUpdatingBankAccount,
  }
}
